<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <div :class="$style.inner">
      <header :class="$style.header">
        <BaseHeading size="h4" as="h2" :class="$style.heading">
          {{ localizedStrings['news'] || 'News' }}
        </BaseHeading>
        <BaseButton :to="`/news`" :class="$style.desktopButton">
          {{ localizedStrings['all-news'] || 'All News' }}
        </BaseButton>
      </header>
      <div ref="swiperRef" :class="['swiper', $style.articles]">
        <div class="swiper-wrapper">
          <div
            v-for="result in computedArticles"
            :key="result?.sys?.id"
            :class="['swiper-slide', $style.slide]"
          >
            <NuxtLinkLocale
              :to="`news/${result?.fields?.path}`"
              target="_blank"
            >
              <div :class="$style.article">
                <AspectRatio ratio="545x378">
                  <BasePicture
                    :src="`${result?.fields?.featuredImage?.fields?.file?.url}?fit=thumb&q=90&w=545`"
                    alt=""
                  />
                </AspectRatio>
                <div :class="$style.articleInfo">
                  <div :class="[$style.meta, 'u-type-p3']">
                    <span
                      v-if="
                        !result?.fields?.hidePublishedDateTime &&
                        result?.fields?.publishedDateTime
                      "
                    >
                      {{
                        format(
                          new Date(result.fields.publishedDateTime),
                          'LLL d, yyyy',
                        )
                      }}
                    </span>
                  </div>
                  <BaseHeading as="h3" size="h6" :class="$style.title">
                    {{ result?.fields?.title }}
                  </BaseHeading>
                  <MarkedParser
                    :md="result?.fields?.excerpt"
                    :class="['u-fs-p1', $style.copy]"
                  />
                </div>
              </div>
            </NuxtLinkLocale>
          </div>
        </div>
      </div>
      <BaseButton :to="`/news`" :class="$style.mobileButton">
        {{ localizedStrings['all-news'] || 'All News' }}
      </BaseButton>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import type { Swiper } from 'swiper'
import swiper from 'swiper'

import type {
  TypeArticleSkeleton,
  TypeContentBlockWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

import 'swiper/css'

const { $client, $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())
const { locale } = useI18n()
const { localizedStrings } = useLocalizedStrings()

defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const DISPLAY_ARTICLES_LIMIT = 8

const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)

const { data: articlesData } = await useAsyncData(
  'heroBlockArticles',
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        limit: 20, // pad to 20 to ensure we get 8
        locale: locale.value,
        include: 10,
        order: ['-fields.publishedDateTime'],
      })

    const items = response?.items ?? []

    const filtered = items.filter((item) => {
      const accessLevel = item.fields?.accessLevel
      const isVisible = $visibility(item.fields?.visibility)
      const hasAccess = $userAccessCheck(accessLevel, highestUserLevel.value)

      return isVisible && hasAccess
    })

    return filtered
  },
  {
    transform(input) {
      return {
        articles: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    // watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
    watch: [() => locale.value],
  },
)

const computedArticles = computed(() =>
  articlesData?.value?.articles?.slice(0, DISPLAY_ARTICLES_LIMIT),
)

onMounted(() => {
  if (swiperRef.value) {
    new swiper(swiperRef.value, {
      slidesPerView: 1.25,
      slidesPerGroup: 1,
      spaceBetween: 15,
      breakpoints: {
        576: {
          slidesPerView: 1.5,
        },
        768: {
          slidesPerView: 2.5,
        },
        1200: {
          slidesPerView: 3.5,
        },
        1440: {
          slidesPerView: 4.5,
          spaceBetween: 20,
        },
      },
    })
  }
})
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--nc-wrap-padding',
    (
      base: 45px 0,
      md: 60px 0,
      lg: 90px 0,
    )
  );

  @include breakpoint-variables(
    '--nc-article-info-padding',
    (
      base: 15px 20px,
      md: 20px 30px,
    )
  );
}

.wrap {
  padding: var(--nc-wrap-padding);
  padding-left: 20px;
}

.inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 1760px;
  margin-left: auto;

  @include media('sm') {
    gap: 30px;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;

  @include media('sm') {
    width: 100%;
    max-width: 1620px;
    margin-right: auto;
    padding-right: 20px;
  }
}

.slide {
  height: auto;
}

.articles {
  width: 100%;
  margin-bottom: 10px;

  @include media('sm') {
    margin-bottom: 0;
  }
}

.meta {
  text-transform: uppercase;
  margin-bottom: 5px;
  color: #828282;
  font-weight: 600;
}

.title,
.copy {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.title {
  margin-bottom: 5px;
}

.copy {
  line-height: clamp(1.6rem, 0.37vw + 1.467rem, 1.8rem);
}

.article {
  height: 100%;
  color: var(--black);
  background: var(--white);
}

.articleInfo {
  display: flex;
  flex-direction: column;
  padding: var(--nc-article-info-padding);
}

.articleInfo {
  margin-top: auto;
}

.desktopButton {
  display: none;

  @include media('sm') {
    display: block;
  }
}

.mobileButton {
  display: block;
  text-align: center;

  @include media('sm') {
    display: none;
  }
}
</style>
